<template>
  <div class="radio-group">
    <div v-for="option in options" :key="option.value" :style="style" class="radio-option" :class="{ disabled: disabled }" @click="!disabled && handleChange(option.value)">
      <input type="radio" :style="getSelectStyle" :id="`radio-${option.value}`" :disabled="disabled" :value="option.value" :checked="modelValue === option.value" class="radio-input" />
      <label :for="`radio-${option.value}`" class="radio-label">
        {{ option.label }}
      </label>
    </div>
    <!-- 其他选项的输入框 -->
    <div class="other-input-wrapper" v-if="showOtherInput">
      <input
        v-if="inputFormat === 1"
        :style="getOtherStyle"
        type="text"
        v-model="otherText"
        @input="handleOtherInput"
        :placeholder="getOtherPlaceholder"
        :disabled="disabled"
        class="other-input"
        :maxlength="200" />
      <text-number-input v-else :style="getOtherStyle" v-model="otherText" @change="handleOtherInput" :placeholder="getOtherPlaceholder" :wordLimit="200" :disabled="disabled" class="other-input" />
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import TextNumberInput from './textNumberInput.vue'
import Toast from '@/h5/components/toast'
import LGSdk from '@/packages/plugins/languageSDK/index'
import { debounce } from 'lodash'
import { computeRatioPx } from '@/packages/plugins/utils'

const props = defineProps({
  modelValue: [String, Number],
  options: {
    type: Array,
    default: () => []
  },
  otherValue: String,
  disabled: Boolean,
  inputFormat: {
    type: Number
  },
  style: {
    type: Object,
    default: () => {}
  }
})

const emits = defineEmits(['change', 'update:modelValue', 'update:otherValue'])

const otherText = ref(props.otherValue || null)

// 是否显示其他输入框
const showOtherInput = computed(() => {
  return props.options.some((option) => option.otherOption && props.modelValue === option.value)
})

// 获取其他选项的配置
const otherOption = computed(() => props.options.find((option) => option.otherOption))

// 获取其他选项的placeholder
const getOtherPlaceholder = computed(() => otherOption.value?.attrs?.placeholder || '')

// 获取其他选项的样式
const getOtherStyle = computed(() => {
  /* 判断 */
  const otherStyle = {
    width: props.style.otherWidth,
    padding: `${props.style.paddingTop} ${props.style.paddingRight} ${props.style.paddingBottom} ${props.style.paddingLeft}`,
    border: 0,
    borderRadius: 0,
    fontSize: props.style.fontSize,
    background: props.style.otherBackground
  }
  if (props.style.otherBorderSize) {
    if (props.style.onlyBorderBottom) {
      otherStyle.borderBottom = props.style.otherBorder
    } else {
      otherStyle.borderRadius = props.style.otherBorderRadius
      otherStyle.border = props.style.otherBorder
    }
  }
  return otherStyle
})

// 获取选择框变量样式
const getSelectStyle = computed(() => {
  return {
    '--select-bg': props.style.selectBg,
    '--selected-bg': props.style.selectedBg,
    '--selected-mark': props.style.selectMark,
    '--border-color': props.style.selectBorderColor,
    '--selected-width': computeRatioPx(18),
    '--selected-height': computeRatioPx(18),
    border: `${computeRatioPx(2)} solid var(--border-color)`,
    '--select-width': computeRatioPx(28),
    '--select-height': computeRatioPx(28),
    '--selected-margin': computeRatioPx(4)
  }
})

const handleChange = (value) => {
  if (!props.disabled) {
    emits('update:modelValue', value)
    emits('change', value)
  }
}

const showToast = debounce(
  (customMessage) => {
    Toast && Toast({ message: customMessage, duration: 1500 })
  },
  500,
  { leading: true, trailing: false }
)

// 处理其他选项的输入
const handleOtherInput = () => {
  if (otherText.value.length >= 200) {
    showToast(LGSdk.t('survey.maxInput'))
  }
  emits('update:otherValue', otherText.value)
}
</script>

<style scoped>
.radio-group {
  display: flex;
  flex-direction: column;
  /* gap: 0.8571rem; */
}

.radio-option {
  display: flex;
  align-items: center;
  transition: all 0.3s;
  cursor: pointer;
  width: max-content;
  max-width: 100%;
  box-sizing: border-box;
}

.radio-option.disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.radio-option.disabled .radio-input {
  cursor: not-allowed;
}

.radio-input {
  margin: 0.2143rem 0.5714rem 0.2143rem 0;
  /* 新增 */
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  outline: none;
  position: relative;
  cursor: pointer;
  border-radius: 50%;
  background: var(--select-bg);
  display: flex;
  align-items: center;
  justify-content: center;
  width: var(--select-width);
  height: var(--select-height);
}

.radio-input:checked {
  width: auto;
  height: auto;
}

.radio-input:checked::before {
  content: '';
  display: block;
  background-color: var(--selected-bg);
  width: var(--selected-width);
  height: var(--selected-height);
  border-radius: 50%;
  position: relative;
  margin: var(--selected-margin);
}

.radio-label {
  overflow-wrap: break-word;
  white-space: normal;
}

.other-input-wrapper {
  width: calc(100% - 1.7143rem);
}

.other-input {
  width: 100%;
  padding: 0.5714rem;
  border: 0.0714rem solid #ddd;
  border-radius: 0.2857rem;
  font-size: 1rem;
}
</style>
